/**
 * queryOptions factories for non-YoY admin hooks.
 *
 * Mirrors the pattern in yoyQueryOptions.ts — standalone factories enable
 * type-safe getQueryData/invalidateQueries calls outside React components.
 *
 * Each factory threads the AbortSignal from QueryFunctionContext into
 * makeApiRequest so in-flight requests are cancelled on key changes.
 */

import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import type { RequestOptions, ApiResponse } from "@/shared/types/request";
import {
  TEST_ACCOUNTS,
  getDateRangeFromTimeframe,
  type UsageRecord,
  type FeedbackItem,
} from "../utils/adminHelpers";
import {
  getDateRangeFromTimeframe as getDashboardDateRange,
  type ExportStats,
  type AttorneyUtilization,
  type PerformanceRow,
  type ExperimentalData,
} from "../types/dashboard";

type MakeApiRequest = (options: RequestOptions) => Promise<ApiResponse>;

// ── Usage Metrics ─────────────────────────────────────────────────────────────

export const usageMetricsOptions = (
  timeframe: string,
  makeApiRequest: MakeApiRequest
) =>
  queryOptions({
    queryKey: ["usage-metrics", timeframe] as const,
    queryFn: async ({ signal }) => {
      const dateRange = getDateRangeFromTimeframe(timeframe);
      const queryParams = dateRange
        ? `?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}&excludeInternal=true`
        : "?excludeInternal=true";
      const response = await makeApiRequest({
        endpoint: `/admin/forEveryone${queryParams}`,
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      const { usageRecords = [], globalExportTimes: exportTimes = {} } =
        response.data || {};
      return {
        usageData: (usageRecords as UsageRecord[]).filter(
          (r) => !TEST_ACCOUNTS.includes(r.matter)
        ),
        globalExportTimes: exportTimes as Record<string, string | null>,
      };
    },
  });

// ── Export Stats ──────────────────────────────────────────────────────────────

export const exportStatsOptions = (
  timeframe: string,
  makeApiRequest: MakeApiRequest
) =>
  queryOptions({
    queryKey: ["export-stats", timeframe] as const,
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const { startDate, endDate } = getDashboardDateRange(timeframe);
      const response = await makeApiRequest({
        endpoint: `/admin/export-stats?startDate=${startDate}&endDate=${endDate}&excludeInternal=true`,
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      const raw = response.data as ExportStats | undefined;
      if (raw) {
        return {
          attorneys: raw.attorneys.filter((a) => !TEST_ACCOUNTS.includes(a.name)),
          totals: raw.totals,
        };
      }
      return { attorneys: [], totals: null };
    },
  });

// ── Feedback ──────────────────────────────────────────────────────────────────

export const feedbackDataOptions = (makeApiRequest: MakeApiRequest) =>
  queryOptions({
    queryKey: ["feedback"] as const,
    queryFn: async ({ signal }) => {
      const response = await makeApiRequest({
        endpoint: "/feedback",
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      return (response.data || []) as FeedbackItem[];
    },
  });

// ── Dashboard Metrics ─────────────────────────────────────────────────────────

export const dashboardMetricsOptions = (
  startDate: string | undefined,
  endDate: string | undefined,
  makeApiRequest: MakeApiRequest
) => {
  const dateParams =
    startDate && endDate ? `&startDate=${startDate}&endDate=${endDate}` : "";
  return queryOptions({
    queryKey: ["dashboard-metrics", startDate ?? "default", endDate ?? "default"] as const,
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const response = await makeApiRequest({
        endpoint: `/admin/dashboard-metrics?excludeInternal=true${dateParams}`,
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      return response.data ?? null;
    },
  });
};

// ── Available Weeks ───────────────────────────────────────────────────────────

export const availableWeeksOptions = (makeApiRequest: MakeApiRequest) =>
  queryOptions({
    queryKey: ["available-weeks"] as const,
    queryFn: async ({ signal }) => {
      const response = await makeApiRequest({
        endpoint: "/admin/billing/available-weeks",
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      return response.data?.weeks ?? [];
    },
  });

// ── Timekeeper Performance ────────────────────────────────────────────────────

export const timekeeperPerformanceOptions = (
  asOfDate: string | null,
  makeApiRequest: MakeApiRequest
) =>
  queryOptions({
    queryKey: ["timekeeper-perf", asOfDate ?? "latest"] as const,
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const param = asOfDate
        ? `?asOfDate=${asOfDate}&excludeInternal=true`
        : "?excludeInternal=true";
      const response = await makeApiRequest({
        endpoint: `/admin/billing/rolling-performance${param}`,
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      const raw = response.data ?? null;
      if (!raw) return null;
      return {
        ...raw,
        rows: raw.rows.filter(
          (r: PerformanceRow) => !TEST_ACCOUNTS.includes(r.prof_name)
        ),
      };
    },
  });

// ── Experimental ─────────────────────────────────────────────────────────────

export const experimentalOptions = (makeApiRequest: MakeApiRequest) =>
  queryOptions({
    queryKey: ["experimental-all"] as const,
    queryFn: async ({ signal }) => {
      const response = await makeApiRequest({
        endpoint: "/admin/billing/experimental",
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      return (response.data ?? null) as ExperimentalData | null;
    },
  });

// ── Utilization ───────────────────────────────────────────────────────────────

export const utilizationOptions = (
  startDate: string,
  endDate: string,
  makeApiRequest: MakeApiRequest
) =>
  queryOptions({
    queryKey: ["utilization", startDate, endDate] as const,
    // keepPreviousData: shows old date range data at 60% opacity while new
    // date range loads — prevents the content from disappearing on filter change
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const response = await makeApiRequest({
        endpoint: `/admin/billing/utilization?startDate=${startDate}&endDate=${endDate}&excludeInternal=true`,
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      const raw = response.data ?? null;
      if (!raw) return null;
      return {
        ...raw,
        attorneys: raw.attorneys.filter(
          (a: AttorneyUtilization) => !TEST_ACCOUNTS.includes(a.prof_name)
        ),
      };
    },
  });
